// ─── Timezone Utilities ────────────────────────────────────────────────────────
// Provides timezone-aware time range computation and date formatting.
// Uses the built-in Intl API — no external libraries required.

export type TimeRangePreset = '1h' | '3h' | '6h' | '12h' | '24h' | '3d' | '7d' | '30d';

export interface TimeRange {
  preset: TimeRangePreset;
  from: number; // epoch ms
  to: number;   // epoch ms
  label: string; // e.g., "Last 24 hours"
}

export interface ComparisonPeriod {
  key: 'yesterday' | 'lastWeek' | 'lastMonth';
  label: string; // e.g., "vs Yesterday"
  from: number; // epoch ms
  to: number;    // epoch ms
}

// ─── Time Range Presets ───────────────────────────────────────────────────────

export const TIME_RANGE_PRESETS: { key: TimeRangePreset; label: string; minutes: number }[] = [
  { key: '1h',  label: '1H',   minutes: 60 },
  { key: '3h',  label: '3H',   minutes: 180 },
  { key: '6h',  label: '6H',   minutes: 360 },
  { key: '12h', label: '12H',  minutes: 720 },
  { key: '24h', label: '24H',  minutes: 1440 },
  { key: '3d',  label: '3D',   minutes: 4320 },
  { key: '7d',  label: '7D',   minutes: 10080 },
  { key: '30d', label: '30D',  minutes: 43200 },
];

export const COMPARISON_PERIODS: { key: ComparisonPeriod['key']; label: string }[] = [
  { key: 'yesterday', label: 'vs Yesterday' },
  { key: 'lastWeek',  label: 'vs Last Week' },
  { key: 'lastMonth', label: 'vs Last Month' },
];

// ─── Time Range Computation ────────────────────────────────────────────────────

/**
 * Compute the from/to epoch ms for a time range preset, adjusted for timezone.
 * The "to" is always "now", and "from" is computed by subtracting the preset duration.
 * Timezone affects how day boundaries are calculated (for daily aggregation).
 */
export function getTimeRange(preset: TimeRangePreset, _timezone?: string): TimeRange {
  const now = Date.now();
  const minutes = TIME_RANGE_PRESETS.find(p => p.key === preset)?.minutes ?? 1440;
  const from = now - minutes * 60 * 1000;

  const label = minutes < 1440
    ? `Last ${(minutes / 60).toFixed(0)} hour${minutes / 60 === 1 ? '' : 's'}`
    : minutes < 10080
      ? `Last ${(minutes / 1440).toFixed(0)} day${minutes / 1440 > 1 ? 's' : ''}`
      : `Last ${(minutes / 10080).toFixed(0)} week${minutes / 10080 > 1 ? 's' : ''}`;

  return { preset, from, to: now, label };
}

/**
 * Compute the previous period for comparison.
 * For "yesterday": same duration as current range, ending where current range starts.
 * For "lastWeek": 7 days before current range.
 * For "lastMonth": 30 days before current range.
 */
export function getComparisonPeriod(
  comparisonKey: ComparisonPeriod['key'],
  currentRange: TimeRange,
): ComparisonPeriod {
  const duration = currentRange.to - currentRange.from;

  switch (comparisonKey) {
    case 'yesterday': {
      const from = currentRange.from - duration;
      const to = currentRange.from;
      return { key: 'yesterday', label: 'vs Yesterday', from, to };
    }
    case 'lastWeek': {
      const from = currentRange.from - 7 * 24 * 60 * 60 * 1000;
      const to = currentRange.from;
      return { key: 'lastWeek', label: 'vs Last Week', from, to };
    }
    case 'lastMonth': {
      const from = currentRange.from - 30 * 24 * 60 * 60 * 1000;
      const to = currentRange.from;
      return { key: 'lastMonth', label: 'vs Last Month', from, to };
    }
  }
}

// ─── Date Formatting ───────────────────────────────────────────────────────────

/**
 * Format an epoch ms timestamp in the given timezone.
 */
export function formatInTimezone(
  epochMs: number,
  timezone: string,
  format: 'full' | 'date' | 'time' | 'short' = 'short',
): string {
  const date = new Date(epochMs);
  const tz = timezone || getDefaultTimezone();

  const optionsMap: Record<string, Intl.DateTimeFormatOptions> = {
    full: {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: tz,
    },
    date: {
      year: 'numeric', month: 'short', day: 'numeric',
      timeZone: tz,
    },
    time: {
      hour: '2-digit', minute: '2-digit',
      timeZone: tz,
    },
    short: {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: tz,
    },
  };

  try {
    return date.toLocaleString('en-US', optionsMap[format]);
  } catch {
    // Fallback if timezone is invalid
    return date.toLocaleString('en-US', optionsMap[format].timeZone ? { ...optionsMap[format], timeZone: undefined } : optionsMap[format]);
  }
}

/**
 * Get the timezone abbreviation (e.g., "PST", "JST", "GMT+9").
 */
export function getTimezoneAbbreviation(timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: timezone,
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value ?? timezone.split('/').pop() ?? timezone;
  } catch {
    return timezone.split('/').pop() ?? timezone;
  }
}

// ─── UTC Offset Timezones ───────────────────────────────────────────────────────

/**
 * Get the browser's default timezone (IANA name).
 */
export function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get a list of UTC offset timezones in GMT+X format.
 * Returns all whole-hour offsets from UTC-12 to UTC+14.
 * Auto-detect resolves to the user's actual offset but displays as GMT+X.
 */
export interface UtcOffsetTimezone {
  value: string;   // 'UTC+8' format used internally
  label: string;   // 'GMT+8' for display
  offset: number;  // numeric offset in hours
}

export function getUtcOffsetTimezones(): UtcOffsetTimezone[] {
  const offsets: UtcOffsetTimezone[] = [];
  
  // Generate UTC-12 to UTC+14 (whole hours only)
  for (let offset = -12; offset <= 14; offset++) {
    const sign = offset >= 0 ? '+' : '';
    const label = offset === 0 ? 'GMT+0' : `GMT${sign}${offset}`;
    const value = `UTC${sign}${offset}`;
    offsets.push({ value, label, offset });
  }
  
  return offsets;
}

/**
 * Get the user's current UTC offset as a GMT+X string.
 * This is used to auto-detect and default the timezone selector.
 */
export function getCurrentUtcOffset(): UtcOffsetTimezone {
  const now = new Date();
  const offsetHours = -now.getTimezoneOffset() / 60; // Offset is opposite of what you'd expect
  
  // More reliable: calculate from Intl
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  
  let offset = offsetHours;
  if (tzPart) {
    // Parse offset like "GMT+8" or "GMT-5"
    const match = tzPart.value.match(/GMT([+-])(\d+)/);
    if (match) {
      offset = parseInt(match[1] + match[2]);
    }
  }
  
  const sign = offset >= 0 ? '+' : '';
  return {
    value: `UTC${sign}${offset}`,
    label: offset === 0 ? 'GMT+0' : `GMT${sign}${offset}`,
    offset,
  };
}

/**
 * Compute percentage change between current and previous values.
 * Returns null if previous is 0 (can't divide by zero).
 */
export function computePercentageChange(current: number, previous: number): number | null {
  if (previous === 0) {
    return current > 0 ? 100 : null; // If previous was 0 and current > 0, that's +100%
  }
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * Determine if a behavior increase is concerning.
 * For most behaviors (spin, toilet, scratch), increase = concerning.
 * For rest, increase could be concerning (too much rest).
 * For total events, neutral.
 */
export type TrendDirection = 'up' | 'down' | 'neutral';

export function getTrendDirection(percentageChange: number | null): TrendDirection {
  if (percentageChange === null) return 'neutral';
  if (percentageChange > 5) return 'up';
  if (percentageChange < -5) return 'down';
  return 'neutral';
}

export function isIncreaseConcerning(behaviorKey: string): boolean {
  // For these behaviors, an increase is concerning
  const concerningBehaviors = ['scratchCount', 'lyingDuration', 'sittingDuration', 'standingDuration', 'walkCount'];
  return concerningBehaviors.includes(behaviorKey);
}